import { useState, useEffect, useMemo } from 'react';
import Papa from 'papaparse';

import Logger from '@app/utils/logger';
import { Container } from '@app/components/Container';
import { PageHeader } from '@app/components/PageHeader';
import { LoadingSpinner } from '@app/components/common/LoadingSpinner';
import { BaseForecastModel, JUA_MODELS } from '@app/common/static-data/models';
import { Selector } from '@app/components/common/Selector';

import { EChartsBenchmarkChart } from './components/EChartsBenchmarkChart';
import { BenchmarkNotes } from './components/BenchmarkNotes';
import { filterDataByVariable } from './components/benchmarkChartHelpers';
import { calculateSkillScores } from './utils/benchmarkCalculations';

const METRICS = ['RMSE', 'MBE'] as const;
const MODES = ['absolute', 'skill'] as const;
const REGIONS = ['Global', 'Europe', 'North America'] as const;
const DEFAULT_REFERENCE_MODEL = 'ECMWF IFS';

// Only include models that have benchmark CSV data available
// Available CSV files: benchmarks-ept-1-5.csv, benchmarks-ept-2.csv, benchmarks-ept-1-5-early.csv
const BENCHMARK_MODELS = JUA_MODELS.filter((model) =>
  ['ept1_5', 'ept1_5_early', 'ept2'].includes(model.id),
);

export default function Benchmarks() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedModel, setSelectedModel] = useState<BaseForecastModel>(BENCHMARK_MODELS[0]);
  const [selectedRegion, setSelectedRegion] = useState<(typeof REGIONS)[number]>('Global');
  const [selectedMetric, setSelectedMetric] = useState<(typeof METRICS)[number]>('RMSE');
  const [selectedVariable, setSelectedVariable] = useState('Wind Speed 10m [m s⁻¹]');
  const [mode, setMode] = useState<'absolute' | 'skill'>('absolute');
  const [referenceModel, setReferenceModel] = useState<string>(DEFAULT_REFERENCE_MODEL);

  const getDateRangeDescription = (model: BaseForecastModel) => {
    return model.benchmarkPeriod || '';
  };

  const availableModes = MODES.filter((mode) => {
    if (selectedMetric === 'MBE' && mode === 'skill') return false;
    return true;
  });

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        // Map models to their corresponding CSV files
        const csvFilenames: Record<string, string> = {
          ept1_5: 'benchmarks-ept-1-5.csv',
          ept2: 'benchmarks-ept-2.csv',
          ept1_5_early: 'benchmarks-ept-1-5-early.csv',
        };

        const filename = csvFilenames[selectedModel.id];
        if (!filename) {
          throw new Error(`No benchmark data available for model: ${selectedModel.id}`);
        }
        const response = await fetch(`/assets/benchmark-data/${filename}`);

        if (!response.ok) {
          throw new Error(`Failed to load ${filename}`);
        }

        const csvText = await response.text();
        const result = Papa.parse(csvText, {
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
        });

        // Update data directly without clearing it first or using setTimeout
        setData(result.data);
      } catch (error) {
        Logger.error('Failed to load benchmark data', error);
        setError('Failed to load benchmark data. Please try again later.');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [selectedModel]);

  // Get list of available models in the current data for the selected variable
  const availableModels = useMemo(() => {
    if (!data.length || !selectedVariable) return [];

    // Filter data for the selected region, metric, and variable
    const filteredData = data.filter(
      (row) => row.Region === selectedRegion && row.Metric === selectedMetric,
    );

    // Apply variable filtering to get only rows with valid values for the selected variable
    const validData = filterDataByVariable(filteredData, selectedVariable);

    // Get the unique model names from the filtered data
    return Array.from(new Set(validData.map((item) => item.Model))).filter(
      (model) => !model.includes('AIFS') && model !== 'ECMWF ENS Mean',
    );
  }, [data, selectedVariable, selectedRegion, selectedMetric]);

  // Effect to set the default reference model when available models change
  useEffect(() => {
    if (availableModels.length > 0) {
      if (availableModels.includes(DEFAULT_REFERENCE_MODEL)) {
        setReferenceModel(DEFAULT_REFERENCE_MODEL);
      } else {
        // If ECMWF IFS is not available, use first model that's not the currently selected model
        const otherModels = availableModels.filter((model) => model !== selectedModel.label);
        if (otherModels.length > 0) {
          setReferenceModel(otherModels[0]);
        } else if (availableModels.length > 0) {
          // Fallback to first available model if no other options
          setReferenceModel(availableModels[0]);
        }
      }
    }
  }, [availableModels, selectedModel]);

  const processedData = useMemo(() => {
    if (!data.length || !selectedVariable) return [];

    // Filter by region, metric, and exclude AIFS models and ECMWF ENS Mean
    let filteredData = data.filter(
      (row) =>
        row.Region === selectedRegion &&
        row.Metric === selectedMetric &&
        !row.Model.includes('AIFS') &&
        row.Model !== 'ECMWF ENS Mean',
    );

    // Apply variable filtering to get only rows with valid values
    filteredData = filterDataByVariable(filteredData, selectedVariable);

    // For skill mode, verify the reference model exists with valid data
    if (mode === 'skill') {
      const referenceModelExists = filteredData.some((row) => row.Model === referenceModel);
      if (!referenceModelExists) {
        Logger.warn(
          `Reference model ${referenceModel} has no data for ${selectedVariable}, using absolute mode`,
        );
        return filteredData; // Return absolute data when reference model is missing
      }

      // Check if reference model has enough valid data points
      const referenceDataPoints = filteredData.filter(
        (row) =>
          row.Model === referenceModel &&
          row[selectedVariable] !== null &&
          !isNaN(row[selectedVariable]),
      );

      if (referenceDataPoints.length === 0) {
        Logger.warn(
          `Reference model ${referenceModel} has no valid data points for ${selectedVariable}, using absolute mode`,
        );
        return filteredData;
      }

      // Calculate skill scores
      const skillData = calculateSkillScores(filteredData, referenceModel, selectedVariable);

      // After calculating skill scores, filter out rows where the result is null
      return filterDataByVariable(skillData, selectedVariable);
    }

    // For absolute mode, just return the filtered data
    return filteredData;
  }, [data, selectedRegion, selectedMetric, selectedVariable, mode, referenceModel]);

  // Get variables that have valid data for the current region and metric
  const variables = useMemo(() => {
    if (data.length === 0) return [];

    // Get all potential variables (excluding metadata fields)
    const allVariables = Object.keys(data[0]).filter(
      (key) => !['Region', 'Metric', 'Prediction timedelta', 'Model'].includes(key),
    );

    // Filter data for current region and metric
    const filteredData = data.filter(
      (row) => row.Region === selectedRegion && row.Metric === selectedMetric,
    );

    // For each variable, check if there's at least one model with valid data
    return allVariables.filter((variable) => {
      // Check if any model has valid data for this variable
      return filteredData.some(
        (row) => row[variable] !== null && row[variable] !== undefined && !isNaN(row[variable]),
      );
    });
  }, [data, selectedRegion, selectedMetric]);

  // Reset or update the selected variable when data changes (after model switch)
  useEffect(() => {
    if (data.length === 0 || variables.length === 0) return;

    // Check if current variable is available in the new data
    const hasCurrentVar = variables.includes(selectedVariable);

    // If current variable is not available, select the first available one
    if (!hasCurrentVar && variables.length > 0) {
      setSelectedVariable(variables[0]);
    }
  }, [data, variables, selectedVariable]);

  if (loading) {
    return (
      <Container>
        <div className="flex items-center justify-center min-h-[500px]">
          <LoadingSpinner size="lg" />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title="Model Benchmarks"
        description={`Benchmarking data for ${selectedModel.label} (${getDateRangeDescription(selectedModel)}) against ERA5`}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <div>
          <Selector
            label="Model"
            value={selectedModel.id}
            data-ph-capture-attribute="dropdown-benchmarks-model"
            onChange={(value) =>
              setSelectedModel(
                BENCHMARK_MODELS.find((model) => model.id === (value as string)) ??
                  BENCHMARK_MODELS[0],
              )
            }
            options={BENCHMARK_MODELS.map((model) => ({
              id: model.id,
              label: model.label,
              metadata: {
                Resolution: model.resolution,
                Updates: model.updateFrequency,
              },
            }))}
          />
        </div>

        <div>
          <Selector
            label="Region"
            data-ph-capture-attribute="dropdown-benchmarks-region"
            options={[...REGIONS].map((region) => ({
              id: region,
              label: region,
            }))}
            value={selectedRegion}
            onChange={(value) => setSelectedRegion(value as (typeof REGIONS)[number])}
          />
        </div>

        <div>
          <Selector
            label="Metric"
            data-ph-capture-attribute="dropdown-benchmarks-metric"
            options={[...METRICS].map((metric) => ({
              id: metric,
              label: metric,
              description: metric === 'RMSE' ? 'Root Mean Square Error' : 'Mean Bias Error',
            }))}
            value={selectedMetric}
            onChange={(value) => {
              setSelectedMetric(value as (typeof METRICS)[number]);
              if ((value as (typeof METRICS)[number]) === 'MBE') setMode('absolute');
            }}
          />
        </div>

        <div>
          <Selector
            label="Variable"
            data-ph-capture-attribute="dropdown-benchmarks-variable"
            options={variables.map((variable) => ({
              id: variable,
              label: variable,
            }))}
            value={selectedVariable}
            onChange={(value) => setSelectedVariable(value as string)}
          />
        </div>

        <div>
          <Selector
            label="Mode"
            data-ph-capture-attribute="dropdown-benchmarks-mode"
            options={[...availableModes].map((mode) => ({
              id: mode,
              label: mode.charAt(0).toUpperCase() + mode.slice(1),
              description:
                mode === 'absolute' ? 'Raw error metrics' : 'Error relative to reference model',
            }))}
            value={mode}
            onChange={(value) => setMode(value as 'absolute' | 'skill')}
          />
        </div>

        <div className={mode !== 'skill' ? 'hidden' : ''}>
          <Selector
            label="Reference Model"
            data-ph-capture-attribute="dropdown-benchmarks-reference-model"
            options={availableModels.map((model) => ({
              id: model,
              label: model,
            }))}
            value={referenceModel}
            onChange={(value) => setReferenceModel(value as string)}
          />
        </div>
      </div>

      <div className="bg-white dark:bg-theme-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <EChartsBenchmarkChart
          data={processedData}
          selectedVariable={selectedVariable}
          mode={mode}
          referenceModel={referenceModel}
        />
      </div>

      <BenchmarkNotes mode={mode} model={selectedModel} />
    </Container>
  );
}
